import {
  BreadcrumbItem,
  BreadcrumbLink,
  HStack,
  Skeleton,
  SkeletonText,
  VStack,
} from "@chakra-ui/react";
import React, { useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BusinessBreadcrumbs } from "../../components/Breadcrumbs/BusinessBreadcrumbs";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { MembershipLoyaltyConfigurationForm } from "../../forms/MembershipLoyaltyConfigurationForm";
import { useAdminApiClient } from "../../hooks/useApiClient";
import { useAdminApiRequest } from "../../hooks/useApiRequest";
import { useToast } from "../../hooks/useToast";

export const IntegrationLoyaltyConfiguration = () => {
  const { businessId, integrationId } = useParams();
  const navigate = useNavigate();
  const apiClient = useAdminApiClient();
  const { showErrorToast, showSuccessToast } = useToast();

  const { data: integration, isLoading } = useAdminApiRequest(
    (apiClient) => {
      if (!businessId || !integrationId) {
        return null;
      }
      return apiClient.getBusinessIntegration(businessId, integrationId);
    },
    [businessId, integrationId]
  );
  const onSuccess = useCallback(() => {
    showSuccessToast("Membership management service configuration updated.");
    navigate(
      `/admin/businesses/${businessId}/integrations/${integration?.Meta?.Id}`,
      { replace: true }
    );
  }, [showSuccessToast, businessId, integration]);

  const onFailure = useCallback(
    (message?: string) => {
      showErrorToast(
        message ?? "Membership management service configuration update failed."
      );
    },
    [showErrorToast]
  );
  return (
    <>
      <PageHeading>
        <BusinessBreadcrumbs>
          <BreadcrumbItem>
            <BreadcrumbLink
              as={Link}
              to={`/admin/businesses/${businessId}/integrations`}
            >
              Integrations
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <BreadcrumbLink
              as={Link}
              to={`/admin/businesses/${businessId}/integrations/${integration?.id}`}
            >
              {integration?.IntegrationName}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <BreadcrumbLink
              as={Link}
              to={`/admin/businesses/${businessId}/integrations/${integration?.id}/membership-loyalty-configuration`}
            >
              Membership Loyalty Service Configuration
            </BreadcrumbLink>
          </BreadcrumbItem>
        </BusinessBreadcrumbs>
        <PageHeading.Title>
          Membership Loyalty Service Configuration
        </PageHeading.Title>
      </PageHeading>
      <PageContent>
        {(isLoading && (
          <VStack
            w="100%"
            p={6}
            boxShadow="lg"
            alignItems="flex-start"
            spacing={4}
          >
            <Skeleton w="100px" height="40px" />
            <SkeletonText w="100%" mt="4" noOfLines={20} spacing="4" />
            <HStack w="100%">
              <Skeleton w="80px" height="40px" />
              <Skeleton w="80px" height="40px" />
              <Skeleton w="80px" height="40px" />
            </HStack>
          </VStack>
        )) || (
          <MembershipLoyaltyConfigurationForm
            businessId={businessId!}
            integration={integration}
            apiClient={apiClient}
            onCancel={() => {
              navigate(
                `/admin/businesses/${businessId}/integrations/${integration?.Meta?.Id}`
              );
            }}
            onSuccess={onSuccess}
            onFailure={onFailure}
          />
        )}
      </PageContent>
    </>
  );
};
